export interface RelationshipArchetype {
  id: string;
  label: string;
  emoji: string;
  description: string;
  defaultPowerDynamic: "equal" | "they_have_authority" | "you_have_authority";
  defaultCommunicationStyle: "direct_blunt" | "diplomatic_warm" | "formal_structured";
  defaultFamiliarityLevel: "stranger" | "acquaintance" | "close";
  defaultSettings: {
    tone?: string;
    format?: string;
    replyLength?: string;
    formality?: string;
  };
}

export const RELATIONSHIP_ARCHETYPES: RelationshipArchetype[] = [
  {
    id: "boss",
    label: "My Boss",
    emoji: "👔",
    description: "Your manager or direct supervisor",
    defaultPowerDynamic: "they_have_authority",
    defaultCommunicationStyle: "formal_structured",
    defaultFamiliarityLevel: "acquaintance",
    defaultSettings: { tone: "professional", format: "email", replyLength: "medium", formality: "professional" },
  },
  {
    id: "parent",
    label: "A Parent",
    emoji: "👪",
    description: "Mom, Dad, or a parental figure",
    defaultPowerDynamic: "they_have_authority",
    defaultCommunicationStyle: "diplomatic_warm",
    defaultFamiliarityLevel: "close",
    defaultSettings: { tone: "friendly", format: "chat", replyLength: "medium", formality: "casual" },
  },
  {
    id: "partner",
    label: "My Partner",
    emoji: "💑",
    description: "Romantic partner or spouse",
    defaultPowerDynamic: "equal",
    defaultCommunicationStyle: "diplomatic_warm",
    defaultFamiliarityLevel: "close",
    defaultSettings: { tone: "friendly", format: "chat", replyLength: "short", formality: "casual" },
  },
  {
    id: "best_friend",
    label: "Best Friend",
    emoji: "🤝",
    description: "Your closest friend",
    defaultPowerDynamic: "equal",
    defaultCommunicationStyle: "direct_blunt",
    defaultFamiliarityLevel: "close",
    defaultSettings: { tone: "casual", format: "chat", replyLength: "short", formality: "casual" },
  },
  {
    id: "client",
    label: "Client / Customer",
    emoji: "🤝",
    description: "A paying client or customer",
    defaultPowerDynamic: "they_have_authority",
    defaultCommunicationStyle: "formal_structured",
    defaultFamiliarityLevel: "acquaintance",
    defaultSettings: { tone: "professional", format: "email", replyLength: "medium", formality: "professional" },
  },
  {
    id: "recruiter",
    label: "Recruiter",
    emoji: "📋",
    description: "A hiring manager or recruiter",
    defaultPowerDynamic: "they_have_authority",
    defaultCommunicationStyle: "formal_structured",
    defaultFamiliarityLevel: "stranger",
    defaultSettings: { tone: "professional", format: "email", replyLength: "medium", formality: "professional" },
  },
  {
    id: "doctor",
    label: "Doctor / Authority",
    emoji: "🏥",
    description: "A doctor, lawyer, or other authority figure",
    defaultPowerDynamic: "they_have_authority",
    defaultCommunicationStyle: "formal_structured",
    defaultFamiliarityLevel: "acquaintance",
    defaultSettings: { tone: "professional", format: "email", replyLength: "medium", formality: "formal" },
  },
  {
    id: "colleague",
    label: "Colleague",
    emoji: "💼",
    description: "A coworker at your same level",
    defaultPowerDynamic: "equal",
    defaultCommunicationStyle: "diplomatic_warm",
    defaultFamiliarityLevel: "acquaintance",
    defaultSettings: { tone: "friendly", format: "chat", replyLength: "medium", formality: "professional" },
  },
  {
    id: "mentor",
    label: "Teacher / Mentor",
    emoji: "🎓",
    description: "A teacher, mentor, or trusted advisor",
    defaultPowerDynamic: "they_have_authority",
    defaultCommunicationStyle: "diplomatic_warm",
    defaultFamiliarityLevel: "acquaintance",
    defaultSettings: { tone: "empathetic", format: "email", replyLength: "medium", formality: "professional" },
  },
];

export const ARCHETYPE_BY_ID: Record<string, RelationshipArchetype> = Object.fromEntries(
  RELATIONSHIP_ARCHETYPES.map((a) => [a.id, a])
);

export const POWER_DYNAMIC_LABELS: Record<string, string> = {
  equal: "Equal footing",
  they_have_authority: "They have authority",
  you_have_authority: "You have authority",
};

export const COMMUNICATION_STYLE_LABELS: Record<string, string> = {
  direct_blunt: "Direct & blunt",
  diplomatic_warm: "Diplomatic & warm",
  formal_structured: "Formal & structured",
};

export const FAMILIARITY_LEVEL_LABELS: Record<string, string> = {
  stranger: "Stranger",
  acquaintance: "Acquaintance",
  close: "Close",
};
